// What the Overview shows for a scope, built in one place so the Overview page and the organisation home's card agree:
// the headline figures, one list of recommendations from every report, how each model is doing, and the data to fix
import { Injectable } from '@angular/core';
import { AnalysisEdge, AnalysisNode } from '../common/analysis-model';
import { GraphData, NODE_SUPERTYPE } from '../common/linqset-model';
import { assetInputs } from '../information/information-analysis-model';
import { DEFAULT_CURRENCY, defaultCapacityHours, getCurrency, getProjectName, ModelDetails, Organisation, totalModelInsights } from '../common/organisation';
import { buildEpmScorecard, epmComponents, EpmScorecard } from '../epm/epm-scorecard-model';
import { LensKey, modelColours } from '../lenses/lens';
import { FormattingService } from '../linq-utils/formatting/formatting.service';
import { OrganisationModelReader } from '../model-services/organisation-model-reader.service';
import { InsightsService } from '../model-services/insights.service';
import { buildSystemsReport, SystemsReport } from '../systems/systems-model';
import { buildWorkforceReport, WorkforceReport } from '../workforce/workforce-model';
import { buildRecommendations, Recommendation } from './recommendations';
import { Change, changeFormat, changeFrom, measure, Measure } from './comparison';
import { AnalysisScope, filterForScope, isWholeOrganisation, modelsInScope } from './scope';

// a headline figure, and the view that explains it
export interface Headline {
    label: string;
    value: string;
    detail: string;
    tone: 'good' | 'bad' | 'warn' | 'neutral';
    lens: LensKey;
    measure?: Measure;                  // the figure compared with the current state's when a project is looked at
    before?: string;                    // the current state's, written out
    change?: Change;
}

// one row of the models table: how each model in scope is doing
export interface ModelRow {
    key: string;
    name: string;
    colour: string;
    hours: number;
    cost: number;
    automated: number | null;
    overRoles: number;
    manualInterfaces: number;
    exceptionCost: number;              // of the cost, exception work: steps on a detour from the main flow, or marked as such
    exceptionHours: number;
    exceptionActions: number;
    outputs: number;
    recommendations: number;
    urgent: number;
    changes?: { hours?: Change; cost?: Change };    // from the same model in the current state, when a project is looked at
}

export interface OverviewData {
    workspaceName: string;
    scopeDescription: string;           // "the whole organisation", a model's name, or "3 models"
    models: ModelDetails[];
    headlines: Headline[];
    recommendations: Recommendation[];  // changes, strongest first
    dataGaps: Recommendation[];         // the data to fix
    epmDataGaps: string[];
    rows: ModelRow[];
    workforce: WorkforceReport;
    systems: SystemsReport;
    epm: EpmScorecard;
}

@Injectable({ providedIn: 'root' })
export class OverviewService {
    constructor(private insightsService: InsightsService, private formatter: FormattingService, private organisationModel: OrganisationModelReader) {}

    // loads the organisation model and the analysis for the scope's workspace, and builds the overview of its models
    async load(organisation: Organisation, scope: AnalysisScope): Promise<OverviewData> {
        const [organisationModel, analysis] = await Promise.all([
            this.organisationModel.read(organisation._key, scope.workspaceKey),
            this.insightsService.loadAnalysisGraphForFilter(organisation, filterForScope(scope)) as Promise<{ nodes: AnalysisNode[]; edges: AnalysisEdge[] }>,
        ]);
        return this.build(organisation, organisationModel, analysis, scope);
    }

    // a project's overview with each headline figure, and each model's hours and cost, set beside the current state's
    compare(project: OverviewData, currentState: OverviewData, organisation: Organisation) {
        const currency = getCurrency(organisation.currencyCode || DEFAULT_CURRENCY);
        const money = (v: number) => this.formatter.formatAbbreviatedNumber(v, currency);
        const number = (v: number) => this.formatter.formatAbbreviatedNumber(v);
        const format = changeFormat(money, number);
        for (const h of project.headlines) {
            const before = currentState.headlines.find(b => b.label === h.label)?.measure;
            h.change = changeFrom(before, h.measure, format);
            h.before = before ? (before.unit === 'percent' ? `${Math.round(before.amount)}%` : before.unit === 'money' ? money(before.amount) : number(before.amount)) : undefined;
        }
        for (const row of project.rows) {
            const before = currentState.rows.find(r => r.key === row.key);
            row.changes = {
                hours: changeFrom(before && measure(before.hours, 'down', 'hours'), measure(row.hours, 'down', 'hours'), format),
                cost: changeFrom(before && measure(before.cost, 'down', 'money'), measure(row.cost, 'down', 'money'), format),
            };
        }
    }

    build(organisation: Organisation, organisationModel: GraphData, analysis: { nodes: AnalysisNode[]; edges: AnalysisEdge[] }, scope: AnalysisScope): OverviewData {
        const currency = getCurrency(organisation.currencyCode || DEFAULT_CURRENCY);
        const money = (v: number) => this.formatter.formatAbbreviatedNumber(v, currency);
        const number = (v: number) => this.formatter.formatAbbreviatedNumber(v);
        const models = modelsInScope(scope, organisation);
        const workspaceName = getProjectName(organisation, scope.workspaceKey) || 'Current state';
        const whole = isWholeOrganisation(scope);
        const scopeDescription = whole ? 'the whole organisation' : models.length === 1 ? models[0].name : `${models.length} models`;
        const modelNames = Object.fromEntries(organisation.models.map(m => [m._key, m.name]));
        const modelKeys = models.map(m => m._key);
        const inScope = { nodes: analysis.nodes.filter(n => modelKeys.includes(n.modelKey)), edges: analysis.edges.filter(e => modelKeys.includes(e.modelKey)) };

        const scoped = !whole;
        const workforce = buildWorkforceReport({ organisationModel, analysis: inScope, workspaceKey: scope.workspaceKey, modelNames, defaultCapacityHours: defaultCapacityHours(organisation), money, number, scoped });
        const systems = buildSystemsReport({ organisationModel, analysis: inScope, workspaceKey: scope.workspaceKey, modelNames, money, number, scoped });
        const totals = models.length ? totalModelInsights(models, scope.workspaceKey, organisation._key) : undefined;
        const epm = buildEpmScorecard({
            scopeName: models.length === 1 ? models[0].name : `The organisation (${models.length} models)`, workspaceName, modelKeys,
            nodes: inScope.nodes, edges: inScope.edges, components: epmComponents(organisationModel, scope.workspaceKey), modelNames, defaultCapacity: defaultCapacityHours(organisation) > 0,
            totals: { revenue: totals?.outputRevenue || 0, value: totals?.outputValue || 0 }, assets: assetInputs(organisation.informationAssets, totals, organisationModel.nodes),
            currency: currency.code, money, number,
        });

        // the smallest changes (under a hundredth of the strongest) would only be noise
        const all = buildRecommendations({ workforce, systems, epm }).filter(r => r.urgent || r.priority >= 1);
        return {
            workspaceName, scopeDescription, models,
            headlines: this.headlines(workforce, systems, epm, inScope, totals?.outputRevenue || 0, whole, money, number),
            recommendations: all.filter(r => r.lever !== 'data'), dataGaps: all.filter(r => r.lever === 'data'), epmDataGaps: epm.dataGaps,
            rows: this.rows(organisation, models, inScope, workforce, systems, all),
            workforce, systems, epm,
        };
    }

    private headlines(w: WorkforceReport, s: SystemsReport, e: EpmScorecard, analysis: { nodes: AnalysisNode[] }, revenue: number, whole: boolean,
        money: (v: number) => string, number: (v: number) => string): Headline[] {
        const utilisation = w.totals.utilisation;
        const outputs = analysis.nodes.filter(n => n.superType === NODE_SUPERTYPE.INFORMATION).length;
        const friction = e.headlines.find(h => h.label === 'Friction');
        const value = e.headlines.find(h => h.label === 'Net operating value');
        return [
            { label: 'Annual cost', value: money(w.totals.cost + s.totals.cost), detail: `${money(w.totals.cost)} ${whole ? 'people' : 'people working here'} · ${money(s.totals.cost)} systems and infrastructure`, tone: 'neutral', lens: 'people',
                measure: measure(w.totals.cost + s.totals.cost, 'down', 'money') },
            { label: 'Workload against capacity', value: utilisation === null ? '–' : `${Math.round(utilisation * 100)}%`,
                detail: w.totals.overRoles ? `${w.totals.overRoles} ${w.totals.overRoles === 1 ? 'role' : 'roles'} over capacity` : 'No role over capacity',
                tone: w.totals.overRoles ? 'bad' : utilisation !== null && utilisation < 0.5 ? 'warn' : 'good', lens: 'people',
                measure: utilisation === null ? undefined : measure(utilisation * 100, 'none', 'percent') },
            { label: 'Work automated', value: s.totals.automatedShare === null ? '–' : `${Math.round(s.totals.automatedShare * 100)}%`,
                detail: `${number(s.totals.manualInterfaces)} manual ${s.totals.manualInterfaces === 1 ? 'interface' : 'interfaces'} · ${number(s.totals.actions.manual)} steps outside systems`,
                tone: s.totals.manualInterfaces ? 'warn' : 'good', lens: 'systems',
                measure: s.totals.automatedShare === null ? undefined : measure(s.totals.automatedShare * 100, 'up', 'percent') },
            { label: 'Carbon a year', value: `${number(s.totals.carbon)} kg`, detail: 'CO2e from systems and infrastructure', tone: 'neutral', lens: 'systems',
                measure: measure(s.totals.carbon, 'down', 'number') },
            { label: 'Outputs and revenue', value: number(outputs), detail: revenue ? `${money(revenue)} revenue a year` : 'No revenue set on outputs', tone: revenue ? 'good' : 'neutral', lens: 'actions',
                measure: measure(outputs, 'none', 'count') },
            { label: friction?.label || 'Friction', value: friction?.value || '–', detail: this.frictionDetail(e, money) || friction?.detail || value?.detail || '', tone: friction?.tone || 'neutral', lens: 'actions',
                // friction is in money or hours, never the scorecard's time per item
                measure: friction?.measure && { ...friction.measure } as Measure },
        ];
    }

    // friction as the scorecard splits it: the dead ends, the exception work on detours from the main flow, the re-keying
    // and the manual bridges between systems
    private frictionDetail(e: EpmScorecard, money: (v: number) => string): string {
        const t = e.evidence?.totals;
        if (!t) return '';
        const deadEnds = e.frictionRows.length;
        const detours = e.detourRows.length;
        const rekeying = e.rekeyRows.length;
        const bridges = (e.informationRisks || []).filter(r => r.kind === 'bridge' && r.alsoFriction).length;
        if (!deadEnds && !detours && !rekeying && !bridges) return 'No dead ends, exception work, re-keying or manual bridges';
        const parts: string[] = [];
        if (deadEnds) parts.push(`${money(t.deadEndCost)} on ${deadEnds} dead ${deadEnds === 1 ? 'end' : 'ends'}`);
        if (detours) parts.push(`${money(t.exceptionCost)} exception work on ${detours} ${detours === 1 ? 'detour' : 'detours'} from the main flow`);
        if (rekeying) parts.push(`${money(t.rekeyCost)} re-keying in ${rekeying} ${rekeying === 1 ? 'step' : 'steps'}`);
        if (bridges) parts.push(`${money(t.bridgeCost)} on ${bridges} manual ${bridges === 1 ? 'bridge' : 'bridges'}`);
        return parts.join(' · ');
    }

    private rows(organisation: Organisation, models: ModelDetails[], analysis: { nodes: AnalysisNode[] }, w: WorkforceReport, s: SystemsReport, recommendations: Recommendation[]): ModelRow[] {
        const colours = modelColours(organisation);
        return models.map(m => {
            const actions = analysis.nodes.filter(n => n.modelKey === m._key && n.superType === NODE_SUPERTYPE.ACTION);
            const automation = s.models.find(x => x.key === m._key);
            const done = automation ? automation.actions.automated + automation.actions.assisted + automation.actions.manual : 0;
            // what is worth doing in the model: the urgent, and changes of at least a tenth of the strongest
            const mine = recommendations.filter(r => r.lever !== 'data' && r.models.includes(m.name) && (r.urgent || r.priority >= 10));
            return {
                key: m._key, name: m.name, colour: colours.get(m._key) || '#a9a8a3',
                hours: actions.reduce((sum, a) => sum + (a.hours || 0), 0), cost: actions.reduce((sum, a) => sum + (a.cost || 0), 0),
                automated: done ? automation!.actions.automated / done : null,
                overRoles: w.roles.filter(r => r.status === 'over' && r.models.includes(m.name)).length,
                manualInterfaces: s.interfaces.filter(i => i.models.includes(m.name) && i.items - i.automatedItems > 0.5).length,
                exceptionCost: actions.filter(a => a.exceptionWork).reduce((sum, a) => sum + (a.cost || 0), 0),
                exceptionHours: actions.filter(a => a.exceptionWork).reduce((sum, a) => sum + (a.hours || 0), 0),
                exceptionActions: actions.filter(a => a.exceptionWork).length,
                outputs: analysis.nodes.filter(n => n.modelKey === m._key && n.superType === NODE_SUPERTYPE.INFORMATION).length,
                recommendations: mine.length, urgent: mine.filter(r => r.urgent).length,
            };
        }).sort((a, b) => b.urgent - a.urgent || b.recommendations - a.recommendations || b.hours - a.hours);
    }
}
